/**
 * Toucan People — one sitting of the review quiz, as plain data the page holds.
 *
 * Learn works through the cards that are due. It takes its cards up front, puts
 * a missed card back in, and ends once every card is cleared or it has asked
 * {@link SESSION_SIZE}, whichever comes first. Revise is extra practice once
 * nothing is due: a fixed number of turns at random, where a hit changes
 * nothing and a miss sends the card to tomorrow's Learn.
 */

import { required } from "toucan";
import {
  dueCards,
  postponeReview,
  recordReview,
  reviewPairs,
} from "./model.ts";
import type { PeopleStore, ReviewCard } from "./model.ts";

/** Turns per session — a rough 5-minute sitting at ~15s a card. */
export const SESSION_SIZE = 20;

/** The miss, of one card in one Learn session, that sets it aside until tomorrow. */
const MISSES_BEFORE_TOMORROW = 3;

export type SessionKind = "learn" | "revise";

export interface Session {
  kind: SessionKind;
  /** The card on show, or null once the session is over. */
  card: ReviewCard | null;
  /** Learn's cards still to clear, the one on show included. */
  left: ReviewCard[];
  /** How many turns the bar counts as done: every answer is one. */
  answered: number;
  /**
   * How many turns the bar counts out of. A Learn miss adds the turn that asks
   * the card again, up to {@link SESSION_SIZE}, so the bar is full exactly when
   * the session is over.
   */
  total: number;
  /** Revise's hits, for the score it ends on. */
  right: number;
  /** Learn's misses so far, by {@link cardKey}. */
  misses: Record<string, number>;
  /** Whoever had a card set aside this session, in the order it happened. */
  tricky: string[];
}

/** What tells one card from another. */
function cardKey(card: ReviewCard): string {
  return `${card.personId}/${card.photo}/${card.direction}`;
}

/**
 * A card to ask next, at random. `lastPersonId` sits the next card out: the
 * same face straight back, or the name just read asked the other way round,
 * tests nothing. Someone alone in the pool is asked again regardless.
 */
function pick(
  cards: ReviewCard[],
  random: () => number,
  lastPersonId?: string,
): ReviewCard | null {
  const others = cards.filter((card) => card.personId !== lastPersonId);
  const pool = others.length > 0 ? others : cards;
  return pool[Math.floor(random() * pool.length)] ?? null;
}

/** Up to `count` of the cards, chosen at random where there are more. */
function sample(
  cards: ReviewCard[],
  count: number,
  random: () => number,
): ReviewCard[] {
  if (cards.length <= count) {
    return cards;
  }
  const shuffled = [...cards];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    const held = required(shuffled[index], "card");
    shuffled[index] = required(shuffled[swap], "card");
    shuffled[swap] = held;
  }
  return shuffled.slice(0, count);
}

/** Which kind of session the button offers, Learn while anything is due. */
export function sessionKind(
  store: PeopleStore,
  personIds: string[],
  now: number = Date.now(),
): SessionKind {
  return dueCards(store, personIds, now).length > 0 ? "learn" : "revise";
}

/**
 * A new session over these people. A Learn with nothing due starts over, and
 * says today is done.
 */
export function startSession(
  store: PeopleStore,
  personIds: string[],
  kind: SessionKind,
  now: number = Date.now(),
  random: () => number = Math.random,
): Session {
  const empty = { answered: 0, right: 0, misses: {}, tricky: [] };
  if (kind === "learn") {
    const left = sample(dueCards(store, personIds, now), SESSION_SIZE, random);
    return {
      ...empty,
      kind,
      left,
      card: pick(left, random),
      total: left.length,
    };
  }
  const card = pick(reviewPairs(store, personIds), random);
  return {
    ...empty,
    kind,
    left: [],
    card,
    total: card === null ? 0 : SESSION_SIZE,
  };
}

/**
 * Learn's grading: a hit clears the card, a miss puts it back in. Cards left
 * when the turns run out stay due for the next session.
 */
function gradeLearn(
  store: PeopleStore,
  session: Session,
  card: ReviewCard,
  correct: boolean,
  now: number,
  random: () => number,
): Session {
  const key = cardKey(card);
  const missed = (session.misses[key] ?? 0) + (correct ? 0 : 1);
  const clears = correct || missed >= MISSES_BEFORE_TOMORROW;
  if (missed >= MISSES_BEFORE_TOMORROW) {
    postponeReview(store, card.personId, card.photo, card.direction, now);
  } else {
    recordReview(
      store,
      card.personId,
      card.photo,
      card.direction,
      correct,
      now,
    );
  }
  const left = clears
    ? session.left.filter((entry) => cardKey(entry) !== key)
    : session.left;
  const tricky =
    missed >= MISSES_BEFORE_TOMORROW && !session.tricky.includes(card.personId)
      ? [...session.tricky, card.personId]
      : session.tricky;
  const answered = session.answered + 1;
  return {
    ...session,
    left,
    card: answered < SESSION_SIZE ? pick(left, random, card.personId) : null,
    answered,
    total: Math.min(SESSION_SIZE, answered + left.length),
    misses: { ...session.misses, [key]: missed },
    tricky,
  };
}

/** Revise's grading: a hit leaves the card alone, a miss sends it to tomorrow. */
function gradeRevise(
  store: PeopleStore,
  personIds: string[],
  session: Session,
  card: ReviewCard,
  correct: boolean,
  now: number,
  random: () => number,
): Session {
  if (!correct) {
    postponeReview(store, card.personId, card.photo, card.direction, now);
  }
  const answered = session.answered + 1;
  return {
    ...session,
    card:
      answered < session.total
        ? pick(reviewPairs(store, personIds), random, card.personId)
        : null,
    answered,
    right: session.right + (correct ? 1 : 0),
  };
}

/** Grade the card on show and move on to the next, writing the result. */
export function gradeSession(
  store: PeopleStore,
  personIds: string[],
  session: Session,
  card: ReviewCard,
  correct: boolean,
  now: number = Date.now(),
  random: () => number = Math.random,
): Session {
  return session.kind === "learn"
    ? gradeLearn(store, session, card, correct, now, random)
    : gradeRevise(store, personIds, session, card, correct, now, random);
}

/** The finished screen's line about who was set aside, or "" for nobody. */
export function trickyLine(names: string[]): string {
  if (names.length === 0) {
    return "";
  }
  const list = new Intl.ListFormat("en-GB").format(names);
  const were = names.length === 1 ? "was" : "were";
  return `${list} ${were} tricky, so they’ll be back tomorrow.`;
}
